// Shared non-component exports; keep component modules valid Fast Refresh boundaries.
import type { AnalyticsPeriod, CustomDateRange, RangeMode, TimeRange } from "@/lib/api";
import { NZ_TIME_ZONE, parseNzDatetimeLocalAsUtcMs, todayNzIso } from "@/lib/nz-time";
import {
  RETENTION_LOOKBACK_DAYS,
  clampCustomStart,
  customRangeExpired,
  retentionFloorIso,
} from "@/lib/retention";

export const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "daily",     label: "Yesterday" },
  { value: "weekly",    label: "Weekly" },
  { value: "monthly",   label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "six_month", label: "6 Month" },
  // Not "All-time" any more: the server floors every query at the retention
  // window, so this range genuinely covers the last 12 months. The wire value
  // stays `all_time` so saved URLs and bookmarks keep working.
  { value: "all_time",  label: "Last 12 months" },
  // Task #365 — last option keeps the button group ordered from
  // smallest fixed window to most flexible.
  { value: "custom",    label: "Custom" },
];

export const RANGE_MODE_OPTIONS: { value: RangeMode; label: string; tip: string }[] = [
  {
    value: "calendar",
    label: "Calendar",
    tip: "Show the in-progress calendar period (week / month / quarter / 6-month block)",
  },
  {
    value: "rolling",
    label: "Rolling",
    tip: "Show a trailing window ending today (7 / 30 / 90 / 180 days)",
  },
];

export const ROLLING_DAYS: Partial<Record<TimeRange, number>> = {
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  six_month: 180,
};

export const TIME_RANGE_TITLES_CALENDAR: Record<TimeRange, string> = {
  daily: "Yesterday",
  weekly: "This week",
  monthly: "This month",
  quarterly: "This quarter",
  six_month: "This 6-month block",
  all_time: "Last 12 months",
  // Overridden below when a custom window is active; only used as a
  // fallback when no dates have been picked yet.
  custom: "Custom range",
};

export const TIME_RANGE_TITLES_ROLLING: Partial<Record<TimeRange, string>> = {
  weekly: "Last 7 days",
  monthly: "Last 30 days",
  quarterly: "Last 90 days",
  six_month: "Last 180 days",
};

export function rangeTitle(
  range: TimeRange,
  mode: RangeMode,
  customRange?: CustomDateRange | null,
): string {
  // Task #365 — "Custom" reads as the picked dates whenever both bounds
  // are present; otherwise fall back to the placeholder title so the
  // header never says "This month" when Custom is the active preset.
  if (range === "custom") {
    // A window that ends before the floor has no overlap with retained data —
    // clamping just the start would advertise an inverted range, so it falls
    // through to the placeholder instead (the server rejects the request too).
    const floor = retentionFloorIso(todayNzIso());
    if (
      customRange?.start &&
      customRange?.end &&
      !customRangeExpired(customRange.end, floor)
    ) {
      const clampedStart = clampCustomStart(customRange.start, floor);
      const startYear = parseUtcDate(clampedStart)?.getUTCFullYear();
      const endYear = parseUtcDate(customRange.end)?.getUTCFullYear();
      const withYear =
        startYear != null && endYear != null && startYear !== endYear;
      const s = fmtDay(clampedStart, withYear);
      const e = fmtDay(customRange.end, withYear);
      if (s && e) return `${s} – ${e}`;
    }
    return TIME_RANGE_TITLES_CALENDAR.custom;
  }
  if (mode === "rolling") {
    return TIME_RANGE_TITLES_ROLLING[range] ?? TIME_RANGE_TITLES_CALENDAR[range];
  }
  return TIME_RANGE_TITLES_CALENDAR[range];
}

export function parseUtcDate(iso: string | null): Date | null {
  if (!iso) return null;
  const dt = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function fmtDay(iso: string | null, withYear = false): string | null {
  const dt = parseUtcDate(iso);
  if (!dt) return null;
  return dt.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: withYear ? "numeric" : undefined,
    timeZone: "UTC",
  });
}

export function dayCount(start: string | null, end: string | null): number | null {
  const s = parseUtcDate(start);
  const e = parseUtcDate(end);
  if (!s || !e) return null;
  const diff = Math.round((e.getTime() - s.getTime()) / 86_400_000);
  return diff > 0 ? diff : null;
}

export function lastInclusiveDay(isoEnd: string | null): string | null {
  const dt = parseUtcDate(isoEnd);
  if (!dt) return null;
  const prev = new Date(dt.getTime() - 86_400_000);
  return prev.toISOString().slice(0, 10);
}

export function compareCaption(
  rangeMode: RangeMode,
  period: AnalyticsPeriod,
  priorPeriod: AnalyticsPeriod | null,
): string | null {
  if (!priorPeriod) return null;
  // Rolling mode is framed as "last N days" because the boundaries shift
  // every day and the absolute dates are noisier than the window length.
  // N is derived from the API-returned period so the caption stays in
  // sync with however the server defines a rolling window.
  if (rangeMode === "rolling") {
    const days = dayCount(period.start, period.end);
    if (days) return `Last ${days} days vs prior ${days} days`;
  }
  // The API returns exclusive end dates (half-open intervals). Convert
  // to the last inclusive day for user-facing display so "Jun 1 – Jun 7"
  // means data through end-of-day Jun 7.
  const curEnd = lastInclusiveDay(period.end);
  const priorEnd = lastInclusiveDay(priorPeriod.end);
  const startsYear = parseUtcDate(period.start)?.getUTCFullYear();
  const priorYear = parseUtcDate(priorPeriod.start)?.getUTCFullYear();
  const withYear = startsYear != null && priorYear != null && startsYear !== priorYear;
  const cs = fmtDay(period.start, withYear);
  const ce = fmtDay(curEnd, withYear);
  const ps = fmtDay(priorPeriod.start, withYear);
  const pe = fmtDay(priorEnd, withYear);
  if (!cs || !ce || !ps || !pe) return null;
  return `${cs} – ${ce} vs ${ps} – ${pe}`;
}

export function nzDateParts(now: Date): {
  year: number;
  month: number;
  day: number;
  weekday: number;
} {
  const fmt = new Intl.DateTimeFormat("en-NZ", {
    timeZone: NZ_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = fmt.formatToParts(now);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  const wdStr = parts.find((p) => p.type === "weekday")?.value ?? "";
  const wdMap: Record<string, number> = {
    Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0,
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    weekday: wdMap[wdStr] ?? 0,
  };
}

export function nzMidnightMs(year: number, month: number, day: number): number | null {
  const norm = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(norm.getTime())) return null;
  const iso = norm.toISOString().slice(0, 10);
  return parseNzDatetimeLocalAsUtcMs(`${iso}T00:00`);
}

export function parseIsoDateParts(
  iso: string | null | undefined,
): { year: number; month: number; day: number } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (Number.isNaN(Date.UTC(year, month - 1, day))) return null;
  return { year, month, day };
}

export function resolvePeriodBoundsMs(
  range: TimeRange,
  mode: RangeMode,
  customRange?: CustomDateRange | null,
  now: Date = new Date(),
): { startMs: number | null; endMs: number | null } {
  const t = nzDateParts(now);
  const todayPlusOneMs = nzMidnightMs(t.year, t.month, t.day + 1);
  if (todayPlusOneMs == null) return { startMs: null, endMs: null };
  // Retention floor, mirroring the server (services/retention_window.py).
  // ``[floor, today]`` inclusive spans exactly RETENTION_LOOKBACK_DAYS days.
  const retentionFloorMs = nzMidnightMs(
    t.year,
    t.month,
    t.day + 1 - RETENTION_LOOKBACK_DAYS,
  );

  if (range === "all_time") {
    // No lower bound, no upper bound. NOTE: the dashboard analytics path now
    // floors all_time at the retention window server-side, so this mirror is
    // deliberately *wider* than that endpoint. It stays unbounded because
    // callers also derive `from` for the agent-aggregate and training-tips
    // endpoints, which still accept an open `from` — narrowing it here would
    // silently change those requests while their server side is unbounded.
    // Tighten this together with those endpoints, not before.
    return { startMs: null, endMs: null };
  }

  if (range === "custom") {
    // User-picked inclusive [start, end] window of NZ calendar dates.
    // Inclusive end → exclusive end (+1 day), clamped to today+1 like
    // every other bounded range.
    const s = parseIsoDateParts(customRange?.start);
    const e = parseIsoDateParts(customRange?.end);
    if (!s || !e) return { startMs: null, endMs: null };
    const startMs = nzMidnightMs(s.year, s.month, s.day);
    const endExclusiveMs = nzMidnightMs(e.year, e.month, e.day + 1);
    if (startMs == null || endExclusiveMs == null)
      return { startMs: null, endMs: null };
    // A deep link can carry a start before the floor; the server clamps it, so
    // the client mirror must too.
    const flooredStartMs =
      retentionFloorMs == null ? startMs : Math.max(startMs, retentionFloorMs);
    return {
      startMs: flooredStartMs,
      endMs: Math.min(endExclusiveMs, todayPlusOneMs),
    };
  }

  if (range === "daily") {
    // "Yesterday": the full prior NZ calendar day. Mode-independent,
    // matching the server (the UI hides the selector for daily).
    return {
      startMs: nzMidnightMs(t.year, t.month, t.day - 1),
      endMs: nzMidnightMs(t.year, t.month, t.day),
    };
  }

  const rollingDays = ROLLING_DAYS[range];
  if (rollingDays == null) return { startMs: null, endMs: null };

  if (mode === "rolling") {
    // Last N days *including today*; exclusive end = today+1.
    return {
      startMs: nzMidnightMs(t.year, t.month, t.day - rollingDays + 1),
      endMs: todayPlusOneMs,
    };
  }

  // Calendar mode — the in-progress NZ calendar block, clamped to
  // today+1 (matches the server's ``min(cur_end_calendar, today+1)``).
  let startMs: number | null;
  let calendarEndMs: number | null;
  if (range === "weekly") {
    // Monday-based ISO week, same as the server's ``today.weekday()``.
    const back = t.weekday === 0 ? 6 : t.weekday - 1;
    startMs = nzMidnightMs(t.year, t.month, t.day - back);
    calendarEndMs = nzMidnightMs(t.year, t.month, t.day - back + 7);
  } else if (range === "monthly") {
    startMs = nzMidnightMs(t.year, t.month, 1);
    calendarEndMs = nzMidnightMs(t.year, t.month + 1, 1);
  } else if (range === "quarterly") {
    const qMonth = t.month - ((t.month - 1) % 3);
    startMs = nzMidnightMs(t.year, qMonth, 1);
    calendarEndMs = nzMidnightMs(t.year, qMonth + 3, 1);
  } else {
    // six_month — Jan–Jun or Jul–Dec block.
    const hMonth = t.month <= 6 ? 1 : 7;
    startMs = nzMidnightMs(t.year, hMonth, 1);
    calendarEndMs = nzMidnightMs(t.year, hMonth + 6, 1);
  }
  if (startMs == null || calendarEndMs == null)
    return { startMs: null, endMs: null };
  return { startMs, endMs: Math.min(calendarEndMs, todayPlusOneMs) };
}
